import React from 'react';
import { InternalStandardProps, TextField, FormLabel } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import { TextFieldProps } from '@mui/material/TextField';
import classnames from 'classnames';
import { assocPath, mergeDeepLeft, path } from 'ramda';
import uuidv4 from 'uuid/v4';
import { parsePathToString } from '../../../crud-core/utils/Path';
import ChipsField, {
  ChipsFieldProps,
  DEFAULT_PATH_TO_LABEL,
  DEFAULT_PATH_TO_VALUE,
} from '../../Field/ChipsField';
import AutoSuggestField, { AutoSuggestFieldProps } from '../../Field/AutoSuggestField';
import DraggableChipsField, { DEFAULT_PATH_TO_ORDER } from '../../Field/DraggableChipsField';
import { Item } from '../../../crud-graphql-containers/Utils/genericTypes';
import { useEditContextForInput } from '../useEditContextForInput';
import { useStyles } from './styles';

export type ChipsInputProps = {
  field: string;
  item?: Item;
  label?: string;
  chipsFieldProps?: Omit<ChipsFieldProps, 'field' | 'classes' | 'pathToLabel' | 'pathToValue'>;
  autoSuggestFieldProps?: Omit<AutoSuggestFieldProps, 'classes'>;
  onChange?: (field: string, value: Object) => void;
  pathToLabel?: string;
  pathToValue?: string;
  pathToOrder?: string;
  textFieldProps?: TextFieldProps;
  draggable?: boolean;
} & Omit<InternalStandardProps<React.HTMLAttributes<HTMLDivElement>>, 'onChange'>;

const ChipsInput: React.FC<ChipsInputProps> = ({
  field,
  label,
  chipsFieldProps,
  autoSuggestFieldProps,
  pathToLabel = DEFAULT_PATH_TO_LABEL,
  pathToValue = DEFAULT_PATH_TO_VALUE,
  pathToOrder = DEFAULT_PATH_TO_ORDER,
  textFieldProps,
  draggable,
  className,
  style,
  onChange,
  item,
}) => {
  const classes = useStyles();

  let value: Object[];
  let handleChange: (items: Object[]) => void;
  if (onChange) {
    value = path(field.split('.'), item) || [];
    handleChange = (items: Object[]) => onChange(field, items);
  } else {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { fieldValue, onChange: onInputChange } = useEditContextForInput<Object[]>(field);
    value = fieldValue || [];
    handleChange = (items: Object[]) => onInputChange(items);
  }

  const [textInputValue, setTextInputValue] = React.useState<string>('');

  const onAdd = (text: string, id?: string) => {
    let newItem = mergeDeepLeft(
      assocPath(pathToValue.split('.'), id ? id : uuidv4(), {}),
      assocPath(pathToLabel.split('.'), text, {}),
    );

    if (draggable) {
      mergeDeepLeft(newItem, assocPath(pathToOrder.split('.'), value.length, {}));
    }

    const x = [...value, newItem];
    handleChange(x);
  };

  const onDelete = (text: string) => {
    const x = value.filter((item) => parsePathToString(pathToValue, item) !== text);
    handleChange(x);
  };

  let props = {
    ...chipsFieldProps,
    field: 'chipItems',
    item: { chipItems: value },
  };

  return (
    <div className={classnames(classes.root, className)}>
      {label && <FormLabel classes={{ root: classes.label }}>{label}</FormLabel>}
      <div className={classes.chipsWrapper}>
        {draggable ? (
          <DraggableChipsField
            className={classes.chipsField}
            chipProps={{ clickable: true }}
            onDelete={(text) => onDelete(text)}
            style={style}
            pathToLabel={pathToLabel}
            pathToValue={pathToValue}
            pathToOrder={pathToOrder}
            onChipsChange={(chipsItems) => handleChange(chipsItems)}
            {...props}
          />
        ) : (
          <ChipsField
            className={classes.chipsField}
            chipProps={{ clickable: true }}
            onDelete={(text) => onDelete(text)}
            style={style}
            pathToLabel={pathToLabel}
            pathToValue={pathToValue}
            {...props}
          />
        )}
        {autoSuggestFieldProps ? (
          <AutoSuggestField
            keyValue={field}
            onSuggestionSelected={async (selectedSuggestion) => {
              if (autoSuggestFieldProps.upsertSuggestion && selectedSuggestion.isNewValue) {
                try {
                  const result = await autoSuggestFieldProps.upsertSuggestion({
                    title: selectedSuggestion.value,
                  });
                  if (result && result.id) {
                    onAdd(result.title, result.id);
                  }
                } catch (error) {
                  console.error('error during upsetMutation : ', error);
                }
              } else {
                onAdd(selectedSuggestion.label, selectedSuggestion.id);
              }
            }}
            {...autoSuggestFieldProps}
          />
        ) : (
          <TextField
            margin="none"
            className={classes.textField}
            variant="standard"
            style={style}
            inputProps={{
              'data-testid': `text-input-${field}`,
            }}
            InputProps={{
              margin: 'none',
            }}
            onChange={(e) => setTextInputValue(e.target.value)}
            onKeyPress={(ev) => {
              if (ev.key === 'Enter' && textInputValue) {
                onAdd(textInputValue);
                ev.preventDefault();
              }
            }}
            value={textInputValue}
            {...textFieldProps}
          />
        )}
      </div>
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default ChipsInput;
